import {
  Button,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import { OrganizationRoleType, useSetResponsiblePersonMutation, type PersonDto } from "../../../autogen/bff-api";
import { useApiError } from "../../../common/errors/useApiError";
import { PersonSelector } from "../../../common/persons/PersonSelector";
import { useSub } from "../../../common/subscription/useSub";

const CREATE_EVENT_ROLES: OrganizationRoleType[] = [
  OrganizationRoleType.Owner,
  OrganizationRoleType.Admin,
  OrganizationRoleType.SourcingCreator,
];

const personName = (p: PersonDto) => [p.firstName, p.lastName].filter(Boolean).join(" ") || p.email;

export const ChangeResponsiblePersonButton = ({
  eventId,
  ownerOrgId,
  currentResponsible,
  variant = "link",
}: {
  eventId: string;
  ownerOrgId: string;
  currentResponsible: PersonDto;
  variant?: "link" | "outline";
}) => {
  const { t } = useTranslation();
  const { canCreateEvents } = useSub();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const toast = useToast();
  const { trigger } = useApiError();
  const [selected, setSelected] = useState<PersonDto | null>(currentResponsible);
  const [setResponsible, { isLoading }] = useSetResponsiblePersonMutation();

  if (!canCreateEvents) return null;

  const changed = !!selected && selected.id !== currentResponsible.id;

  const open = () => {
    setSelected(currentResponsible);
    onOpen();
  };

  const onConfirm = async () => {
    if (!changed || !selected) return;
    try {
      await setResponsible({
        eventId,
        setResponsiblePersonRequest: { responsiblePersonId: selected.id },
      }).unwrap();
      toast({ title: t("Responsible buyer changed"), status: "success", duration: 4000, isClosable: true });
      onClose();
    } catch (e) {
      trigger(e);
    }
  };

  return (
    <>
      <Button variant={variant === "link" ? "link" : "outline"} size="sm" color="smPrimary" onClick={open}>
        {t("Change")}
      </Button>
      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t("Change responsible buyer")}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <Stack spacing="4">
              <Text fontSize="sm" color="smMuted">
                {t("Select a new responsible buyer. Only users who can create events are shown.")}
              </Text>
              <PersonSelector
                size="md"
                organizationIds={[ownerOrgId]}
                roles={CREATE_EVENT_ROLES}
                selectedPerson={selected}
                onChange={setSelected}
                isClearable={false}
              />
              {changed && selected && (
                <Text fontSize="sm" color="smText">
                  {t("New responsible: {{name}}", { name: personName(selected) })}
                </Text>
              )}
            </Stack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr="3" onClick={onClose} isDisabled={isLoading}>
              {t("Cancel")}
            </Button>
            <Button colorScheme="blue" onClick={onConfirm} isLoading={isLoading} isDisabled={!changed}>
              {t("Change responsible buyer")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
};
